import React, { useState } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import {
  calculateExpectedBonusPay,
  calculateRealisticMaxPay,
  calculateTotalPay,
  resolveCompensationConfig,
} from "../../../shared/compensation.js";
import { Button } from "../components/Button";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";

export function Consent({ next }) {
  const [consented, setConsented] = useState(false);
  const game = useGame();
  const player = usePlayer();
  const treatment = game?.get("treatment") || player.get("queueTreatment") || {};
  const design = game?.get("design") || {};
  const lossFrame = isLossFrameDesign({
    payoffFrame: design.payoffFrame ?? treatment.payoffFrame,
  });
  const counterpart = getCounterpartCopy({
    payoffFrame: design.payoffFrame ?? treatment.payoffFrame,
    counterpartTerm: design.counterpartTerm ?? treatment.counterpartTerm,
  });
  const testMode = Boolean((treatment.testMode ?? design.testMode) ?? false);
  const compensationConfig = resolveCompensationConfig({ treatment, design });
  const showUpPayment = compensationConfig.showUpPayment;
  const { expectedBonusPay, expectedTotalPay } =
    calculateExpectedBonusPay(compensationConfig);
  const { expectedMaxPay } = calculateRealisticMaxPay(compensationConfig);
  const minimumTotalPay = calculateTotalPay(compensationConfig);

  return (
    <div className="py-10 max-w-3xl mx-auto px-8 space-y-8">

        <h3 className="text-2xl font-semibold text-gray-900">
          Consent
        </h3>

      <div className="rounded-md border border-gray-200 p-5 space-y-3 text-sm text-gray-700">
        <p>
          This survey is part of a research study conducted by George Loewenstein at Carnegie Mellon University and is funded by Carnegie Mellon University.
        </p>
        <p>
          <strong>Summary</strong><br />
          This study will have you play a mouse-tracking game, chat with a chatbot,
          negotiate with {counterpart.article} {counterpart.term}, and chat with that{" "}
          {counterpart.term}.
        </p>

        <p>
          <strong>Purpose</strong><br />
          The purpose of the research is to understand human judgment and decision making.
        </p>

        <p>
          <strong>Procedures</strong><br />
          You will be paired with another participant for the duration of the study. 
          {lossFrame ? (
            <>
              First, you will each play a mouse-tracking game where your pay is at risk. You follow a moving circle with your cursor to prevent money from being lost.
              The participant (either you or your {counterpart.term}) who does better in this game takes the money saved by both players.
              You will then play a second mouse-tracking game with the same {counterpart.term},
              but this time you and your {counterpart.term} will negotiate over the saved money.
            </>
          ) : (
            <>
              First, you will each play a mouse-tracking game in which you follow a moving circle with your cursor to earn money.
              Earnings from this game are pooled and awarded to the participant who tracked the circle longer.
              You will then play a second mouse-tracking game with the same {counterpart.term},
              but this time you and your {counterpart.term} will negotiate how to split the team's pooled earnings.
            </>
          )}
          You will have a brief conversation with an AI chatbot. 
          Finally, you will complete a brief survey about your experience. 
          The study is expected to take approximately 20 minutes.
        </p>

        <p>
          <strong>Participant Requirements</strong><br />
          Participation in this study is limited to individuals age 18 and older.
        </p>

        <p>
          <strong>Risks</strong><br />
          The risks and discomfort associated with participation in this study are no greater than those ordinarily encountered in daily life or during other online activities.
        </p>

         <p>
          <strong>Benefits</strong><br />
          There may be no personal benefit from your participation in the study, but the knowledge received may be of value to the scientific community.
         </p>

        <p>
          <strong>Compensation & Costs</strong><br />
          {lossFrame ? (
            <>
              You are endowed with ${expectedMaxPay.toFixed(2)} at the start of the study. Throughout the study you may lose some of this money.
              While it is possible to keep all ${expectedMaxPay.toFixed(2)}, on average participants keep ${expectedTotalPay.toFixed(2)}.

              It is not possible for you to earn less than ${minimumTotalPay.toFixed(2)} if you complete the study.
            </>
          ) : (
            <>
              You will receive a ${showUpPayment.toFixed(2)} show-up payment and can earn bonus pay from the games.
              On average, participants earn ${expectedBonusPay.toFixed(2)} in bonus pay and receive ${expectedTotalPay.toFixed(2)} total.
              Participants who perform very well could receive approximately ${expectedMaxPay.toFixed(2)} total.

              It is not possible for you to earn less than ${minimumTotalPay.toFixed(2)} if you complete the study.
            </>
          )}{" "}

          Because you have {counterpart.article} {counterpart.term}, if you leave the study or are inactive for too long, you will not receive compensation.

          If your {counterpart.term} leaves the study or is inactive for too long, you will receive partial compensation for your time.

          There will be no cost to you if you participate in this study.
        </p>

         <p>
          <strong>Future Use of Information</strong><br />
          We will not collect any identifiable information about you in this research. In the future, we may use the data for our future research studies, or we may distribute the data to other researchers for their research studies.  We would do this without getting additional informed consent from you. Sharing of data with other researchers will only be done in such a manner that you will not be identified.
         </p>

         <p>
          <strong>Confidentiality</strong><br />
          The data captured for the research does not include any personally identifiable information about you. Your IP address will not be captured.
        </p>

          <p>
          <strong>Right to Ask Questions & Contact Information</strong><br />
          If you have any questions about this study, you should feel free to ask them by contacting the Principal Investigator now at Dr. George Loewenstein, Professor, Department of Social and Decision Sciences, 208 Porter Hall, 5000 Forbes Ave., Pittsburgh, PA 15213, 412-268-8787, gloew@andrew.cmu.edu. If you have questions later, desire additional information, or wish to withdraw your participation please contact the Principal Investigator by mail, phone or e-mail in accordance with the contact information listed above. 

          If you have questions pertaining to your rights as a research participant, or to report concerns regarding this study, you should contact the Office of Research Integrity and Compliance at Carnegie Mellon University.  Email: irb-review@andrew.cmu.edu. Phone: 412-268-4721.
        </p>

        <p>
          <strong>Voluntary Participation</strong><br />
          Your participation in this research is voluntary. You may discontinue participation at any time during the research activity. You may print a copy of this consent form for your records.
        </p>
      </div>

      <label className="flex items-start space-x-3 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
        />
        <span>I have read the information above, am above 18 years of age, and consent to participate.</span>
      </label>

      <div className="flex items-center gap-3">
        {testMode ? (
          <div data-no-interaction>
            <Button type="button" primary handleClick={next}>
              Bypass page (test)
            </Button>
          </div>
        ) : null}
        <Button handleClick={next} autoFocus disabled={!consented}>
          <p>Continue</p>
        </Button>
      </div>
    </div>
  );
}
